import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from 'react-bootstrap';
import { Gear } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import './GenerateNotificationFeed.css';
import EmptyNotificationsMessage from './EmptyNotificationMessage';
import { feedsConfig } from '../../utils/feedsConfig';
import HomeFeedSection from '../feed/HomeFeedSection';

// ============================================
// CONSTANTS
// ============================================

// Maps preference localStorage keys -> feedsConfig section keys
const TOPIC_TO_SECTION = {
  legislative:    'legislative',
  executive:      'executive',
  judicial:       'judicial',
  defense:        'defense',
  economy:        'economy',
  elections:      'elections',
  environment:    'environment',
  foreignaffairs: 'foreignAffairs',
  health:         'health',
  science_and_tech: 'science',
  spending:       'spending',
  infrastructure: 'infrastructure',
  regulations:    'regulations',
  justice:        'justice',
};

const TOPIC_DISPLAY_NAMES = {
  legislative: 'Legislative', executive: 'Executive', judicial: 'Judicial',
  defense: 'Defense', economy: 'Economy', elections: 'Elections',
  environment: 'Environment', foreignaffairs: 'Foreign Affairs', health: 'Health',
  science_and_tech: 'Science & Tech', spending: 'Spending',
  infrastructure: 'Infrastructure', regulations: 'Regulations', justice: 'Justice',
};

const TOPIC_PREF_KEYS = {
  legislative: 'LegislativeNews', executive: 'ExecutiveNews', judicial: 'JudicialNews',
  defense: 'DefenseNews', economy: 'EconomyNews', elections: 'ElectionNews',
  environment: 'EnvironmentNews', foreignaffairs: 'ForeignAffairsNews', health: 'HealthNews',
  science_and_tech: 'ScienceTechNews', spending: 'SpendingNews',
  infrastructure: 'InfrastructureNews', regulations: 'RegulationsNews', justice: 'JusticeNews',
};

// ============================================
// STYLES
// ============================================

// The country pill, level switcher, Donate button and menu all live in
// IntegratedSearchNavBar now (mounted by NotificationPage), so nothing in this
// sheet describes a fixed bar any more. What's left is the in-page filter row
// that sits directly under the bar: settings gear, search, topic picker.
const styles = `
  .notif-subheader { display: flex; align-items: center; gap: 6px; padding: 8px 10px 4px 10px; width: 100%; }
  .notif-subheader .notif-settings-link { display: flex; align-items: center; text-decoration: none; }
  .notif-topic-group { position: relative; flex-shrink: 0; }
  .notif-topic-dropdown { position: absolute; top: calc(100% + 4px); right: 0; z-index: 1000; background-color: var(--bg-card); border: 1px solid var(--border-secondary); border-radius: 10px; min-width: 180px; max-height: 260px; overflow-y: auto; box-shadow: 0 4px 16px rgba(0,0,0,0.15); }

  /* Match the centered single-column width the rest of the app uses on
     desktop, so the filter row lines up with the feed cards below it. */
  @media (min-width: 768px) {
    .notif-subheader { max-width: 700px; margin: 0 auto; padding: 12px 0 8px 0; }
  }
`;

// ============================================
// TOPIC DROPDOWN
// ============================================

function TopicDropdown({ followedTopics, activeTopic, onSelect }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const label = activeTopic ? (TOPIC_DISPLAY_NAMES[activeTopic] || activeTopic) : 'All Topics';

  return (
    <div className="notif-topic-group feed-filter-group" ref={ref}>
      <button type="button" className="feed-filter-toggle" onClick={() => setIsOpen(o => !o)}>
        <span className="feed-filter-toggle-content">{label}</span>
        <span className="feed-filter-chevron" />
      </button>
      {isOpen && (
        <div className="notif-topic-dropdown feed-filter-dropdown">
          <button
            type="button"
            className={`feed-filter-item ${!activeTopic ? 'active' : ''}`}
            onClick={() => { onSelect(null); setIsOpen(false); }}
          >
            All Topics
          </button>
          <div className="feed-filter-separator" />
          {followedTopics.map(topic => (
            <button
              key={topic}
              type="button"
              className={`feed-filter-item ${activeTopic === topic ? 'active' : ''}`}
              onClick={() => { onSelect(topic); setIsOpen(false); }}
            >
              {TOPIC_DISPLAY_NAMES[topic] || topic}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// FLATTEN FEEDS FOR FOLLOWED TOPICS
// ============================================

function getFeedsForTopics(topicKeys) {
  const result = [];
  topicKeys.forEach((topicKey) => {
    const sectionKey = TOPIC_TO_SECTION[topicKey];
    if (!sectionKey) return;
    const entries = feedsConfig[sectionKey];
    if (!Array.isArray(entries)) return;
    entries.forEach((feed) => {
      if (feed.type === 'normal' && feed.apiEndpoint) {
        result.push(feed);
      } else if (feed.type === 'carousel' && feed.carouselItems) {
        feed.carouselItems.forEach((item) => {
          if (item.type === 'viewall' || item.type === 'member' || !item.apiEndpoint) return;
          result.push({
            ...item,
            feedName: item.feedName || `${feed.departmentTitle} — ${item.title}`,
            buttonName: item.buttonName || feed.buttonName || 'Document',
          });
        });
      }
    });
  });
  return result;
}

// ============================================
// MAIN COMPONENT
// ============================================

function GenerateNotificationFeed() {
  const [activeTopic, setActiveTopic] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Read which topics the user follows
  const followedTopics = useMemo(() => {
    return Object.entries(TOPIC_PREF_KEYS)
      .filter(([, prefKey]) => localStorage.getItem(prefKey) === 'true')
      .map(([topic]) => topic);
  }, []);

  // Get feeds from feedsConfig for active topic(s)
  const feeds = useMemo(() => {
    const topics = activeTopic ? [activeTopic] : followedTopics;
    return getFeedsForTopics(topics);
  }, [activeTopic, followedTopics]);

  // Inject styles
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = styles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const settingsButton = (
    <Link to="/notifications/settings" className="notif-settings-link">
      <Button id="settingsButton"><Gear size={20} /></Button>
    </Link>
  );

  // Nothing followed yet: keep the gear so the empty state has a way through to
  // the settings page, but drop the search and topic picker, which would have
  // nothing to act on.
  if (followedTopics.length === 0) {
    return (
      <>
        <div className="notif-subheader">{settingsButton}</div>
        <EmptyNotificationsMessage />
      </>
    );
  }

  return (
    <>
      <div className="notif-subheader">
        {settingsButton}
        <input
          id="ggSearch2"
          className="form-control"
          type="text"
          placeholder="Search notifications"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
        <TopicDropdown
          followedTopics={followedTopics}
          activeTopic={activeTopic}
          onSelect={(topic) => { setActiveTopic(topic); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        />
      </div>

      <HomeFeedSection
        feeds={feeds}
        itemsPerFeed={10}
        searchQuery={searchQuery}
      />
    </>
  );
}

export default GenerateNotificationFeed;